import React, { useCallback, useEffect, useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Outlet,
  useAsyncValue,
  useLocation,
  useNavigate,
  useRevalidator,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Components
import { SimpleWorkOrder } from '../../../../../components/display/ShowOT/SimpleWorkOrder';

// Styles
import { ButtonTab, TabsHalf, GeneralContainer, TabsContainer } from './styles';

// Types
import { BasicInfoOT } from './types';
import { Role } from '../../../../../types/auth';

// Redux
import { actionTabs, actionTabsUnlockAll } from '../../../../../state/actions/ui/actionTabs';
import { getNewActionTabsState } from '../../../../../state/selectors/ui/tabs';

// Hooks
import { useAuth } from '../../../../../hooks/useAuth';

// Utils
import { roleCanViewBudgets } from '../../../../../utils/rolePermissions';

export default function ActionValidationPage(): JSX.Element {
  const { t } = useTranslation(['forms', 'workOrders']);
  const loaderData = useAsyncValue() as BasicInfoOT;
  const dispatch = useDispatch();
  const selector = useSelector(getNewActionTabsState);
  const location = useLocation();
  const pathNameArray = location.pathname.split('/');
  const pathName = pathNameArray[pathNameArray.length - 1];
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const { user } = useAuth();

  useLayoutEffect(() => {
    if (!loaderData) {
      navigate('/work-orders');
    }
  }, []);

  useEffect(() => {
    // Validación = actuación EXISTENTE → todas las tabs navegables siempre.
    // Antes solo se desbloqueaba en 'partial'; el resto dependía del path
    // acumulado en Redux (actionTabs), que se pierde al recargar (está en
    // blacklist de redux-persist) o al entrar desde Gestión Horaria (cuyo entry
    // no despacha unlockAll) → tabs deshabilitadas, navegación 1-a-1. Este
    // layout corre en CADA entrada a las pestañas Y en reload → robusto.
    // (La creación de actuación usa otro ActionPage, que sigue secuencial.)
    if (loaderData) {
      dispatch(actionTabsUnlockAll());
    }
  }, [loaderData]);

  useEffect(() => {
    // Habilitar automáticamente la tab cuando el usuario navega a ella
    if (!selector.includes(pathName)) {
      dispatch(actionTabs(pathName));
    }
  }, [location]);

  // Actuació bàsica: hereda tripulación y medios del Recorregut de Vigilància,
  // así que Operarios y Maquinaria no son suyos y no se editan aquí (espejo
  // exacto del wizard de creación dentro del recorrido, FR-9.3).
  const isBasicAction = !!loaderData?.isBasicAction;

  // Redirect OBLIGATORIO, no defensivo: la ruta índice de la validación es
  // `<Navigate to="operators-and-vehicles" replace />` (en los TRES árboles —
  // el global, el de campaña y el del recorrido), así que entrar a validar una
  // básica aterriza SIEMPRE en la pestaña que ocultamos. Sin esto la pantalla
  // queda en blanco.
  // También cubre deep-links y el botón atrás. `replace` para no dejar la
  // pestaña oculta en el historial y provocar un rebote.
  //
  // ⚠️ `'location'` a secas, NO `'../location'`: este componente es el element
  // de la ruta `:id`, y React Router resuelve lo relativo contra la RUTA, no
  // contra la URL. `../location` sube un nivel y se come el `:id` →
  // `/actions-validation/location` → bucle de redirecciones. Es el mismo
  // criterio que los `<ButtonTab to={'location'}>` de abajo.
  //
  // `animals` (2026-08-11) rebota a **Materiales**, no a Ubicación: era el
  // PASO TERMINAL, así que quien llega ahí por un enlace viejo o por el
  // historial quiere guardar. Mandarlo al principio le haría recorrer el
  // asistente entero otra vez.
  useEffect(() => {
    if (!isBasicAction) return;
    if (pathName === 'operators-and-vehicles' || pathName === 'machinery') {
      navigate('location', { replace: true });
    }
    if (pathName === 'animals') {
      navigate('materials', { replace: true });
    }
  }, [isBasicAction, pathName]);

  // Peso de cada mitad de la barra = cuántas pestañas pinta. Sin esto se
  // reparten 50/50 y una mitad con una sola pestaña se lleva media barra.
  const showBudgetItems =
    user?.user?.rolActive !== Role.Tablet &&
    roleCanViewBudgets(user?.user?.rolActive);
  const leftTabs =
    (isBasicAction ? 0 : 1) + 2 + (showBudgetItems ? 1 : 0);
  const rightTabs = isBasicAction ? 1 : 3;
  // 4 pestañas o menos caben en una fila: no hace falta partir la barra en
  // tablet, y partirla dejaba a «Materiales» sola en una fila entera.
  const forceRow = leftTabs + rightTabs <= 4;

  const onClickCheckDisabled = useCallback(
    (path: string) => (e: { preventDefault: () => void }) => {
      if (!selector.includes(path)) e.preventDefault();
    },
    [selector]
  );

  const onReFetchData = useCallback(() => {
    revalidator.revalidate();
  }, []);
  return (
    <GeneralContainer>
      {loaderData && (
        <SimpleWorkOrder
          name={t('action.basicInfo')}
          pkInit={loaderData.pkInit}
          pkEnd={loaderData.pkEnd}
          state={loaderData.state}
          indexExpedition={loaderData.indexExpedition}
          revalidatorFunction={onReFetchData}
          validation={user?.user?.rolActive !== Role.RoomTeam} // RoomTeam cannot change action state
          registerEndDate={loaderData.registerEndDate}
          orderId={loaderData.orderId}
          parentOrderState={loaderData.parentOrderState}
          orderDescription={loaderData.orderDescription}
          actionDescription={loaderData.actionDescription}
          // Opt-in: solo la básica lleva contexto de recorrido. La actuación
          // normal pasa `undefined` y la cabecera no cambia.
          routeRoid={isBasicAction ? loaderData.parentRouteRoid : undefined}
          routeId={isBasicAction ? loaderData.routeId : undefined}
          linkedSegmentRoid={
            isBasicAction ? loaderData.linkedSegmentRoid : undefined
          }
        />
      )}

      <TabsContainer $forceRow={forceRow}>
        <TabsHalf
          $tabCount={leftTabs}
          $wideFirstTab={!isBasicAction}
          $forceRow={forceRow}
        >
          {/* Actuació bàsica: operaris/vehicles son del recorrido, no suyos
              (FR-9.3). Ocultos para TODOS los roles — si hay que corregir la
              tripulación se corrige en el recorrido. */}
          {!isBasicAction && (
            <ButtonTab
              to={'operators-and-vehicles'}
              disabled={!selector.includes('operators-and-vehicles')}
            >
              {t('workOrders:tabs.operatorsAndVehicles')}
            </ButtonTab>
          )}
          <ButtonTab
            onClick={onClickCheckDisabled('location')}
            to={'location'}
            disabled={!selector.includes('location')}
          >
            {t('workOrders:tabs.location')}
          </ButtonTab>
          <ButtonTab
            onClick={onClickCheckDisabled('edition')}
            to={'edition'}
            disabled={!selector.includes('edition')}
          >
            {t('workOrders:tabs.actionData')}
          </ButtonTab>
          {showBudgetItems && (
              <ButtonTab
                onClick={onClickCheckDisabled('budget-items')}
                to={'budget-items'}
                disabled={!selector.includes('budget-items')}
              >
                {t('workOrders:budgetItems')}
              </ButtonTab>
            )}
        </TabsHalf>
        <TabsHalf
          $tabCount={rightTabs}
          $wideSecondTab={!isBasicAction}
          $forceRow={forceRow}
        >
          <ButtonTab
            onClick={onClickCheckDisabled('materials')}
            to={'materials'}
            disabled={!selector.includes('materials')}
          >
            {t('workOrders:tabs.materials')}
          </ButtonTab>
          {/* Actuació bàsica: los medios son los del recorrido (FR-9.3). */}
          {!isBasicAction && (
            <ButtonTab
              onClick={onClickCheckDisabled('machinery')}
              to={'machinery'}
              disabled={!selector.includes('machinery')}
            >
              {t('workOrders:tabs.machinery')}
            </ButtonTab>
          )}
          {/* Actuació bàsica: no registra fauna (2026-08-11). Materiales pasa
              a ser su paso terminal y lleva Guardar/Validar. */}
          {!isBasicAction && (
            <ButtonTab
              onClick={onClickCheckDisabled('animals')}
              to={'animals'}
              disabled={!selector.includes('animals')}
            >
              {t('workOrders:tabs.animals')}
            </ButtonTab>
          )}
        </TabsHalf>
      </TabsContainer>
      <Outlet />
    </GeneralContainer>
  );
}
